'use strict';

import { state, isCoach, isFD, isRefAssignor } from './state.js';
import { el, showModal, closeModal } from './dom.js';
import { svgIcon } from './icons.js';
import { isSeasonAcceptingRequests, seasonDisplayName } from './seasons.js';
import { todayET } from './date.js';

const SEEN_PREFIX = 'fw_offseason_seen:';

/**
 * Explains, once per browser session per season, why a coach can't book
 * anything yet.
 *
 * Between seasons coaches log in, find an empty app, and email the Fields
 * Director to ask whether reservations are open. Every request endpoint already
 * refuses them server-side (checkSeasonAcceptsRequests), so nothing is at risk —
 * but the refusal arrives after they've filled out a form, and the app never
 * says why. This answers the question before they have to ask it.
 *
 * Called from router() *after* the view has rendered. Firing earlier would risk
 * the modal being wiped: closeModal() removes every .modal-overlay on the page,
 * so any render that closes a modal would take this one with it.
 */
export function maybeShowOffSeasonModal() {
  // Coaches only. The FD and RA know the season state and manage it — for them
  // this would be an obstacle, not an explanation.
  if (!isCoach() || isFD() || isRefAssignor()) return;
  const season = state.seasons.find(s => s.id === state.currentSeasonId);
  if (!season || isSeasonAcceptingRequests()) return;
  if (alreadySeen(season.id)) return;
  markSeen(season.id);
  showModal(buildOffSeasonModal(season));
}

// Keyed by season rather than a bare flag so a coach who switches to a
// different off-season season in the dropdown gets the explanation for that
// one too, instead of never seeing it again.
function alreadySeen(seasonId) {
  try {
    return sessionStorage.getItem(SEEN_PREFIX + seasonId) === '1';
  } catch {
    // sessionStorage unavailable (private mode, storage disabled). Showing the
    // modal on every navigation is noisy; never showing it defeats the point.
    return false;
  }
}

function markSeen(seasonId) {
  try { sessionStorage.setItem(SEEN_PREFIX + seasonId, '1'); } catch { /* ignore */ }
}

function buildOffSeasonModal(season) {
  const opens = bookingOpensLabel(season);
  const copy = seasonCopy(season);
  return el('div', { className: 'off-season-modal' },
    el('div', { className: 'modal-body' },
      el('div', { className: 'off-season-modal-ball' }, svgIcon('soccerball-loader')),
      el('h3', {}, 'The ball’s still in the bag'),
      el('p', { className: 'off-season-modal-lede' }, ...copy.lede),
      opens
        ? el('div', { className: 'off-season-modal-chip' },
            svgIcon('calendar'), el('span', {}, 'Booking opens ' + opens))
        : null,
      el('div', { className: 'off-season-modal-list' },
        el('p', { className: 'off-season-modal-list-lbl' }, 'In the meantime'),
        meantimeItem('Browse past schedules and game results'),
        meantimeItem('Check your contact info is current in Settings'),
        meantimeItem(copy.lastItem),
      ),
    ),
    el('div', { className: 'modal-footer' },
      el('button', { className: 'btn btn-primary', onclick() { closeModal(); } }, 'Got it'),
    ),
  );
}

// The two non-accepting states need different words. Before a season's
// reservations open, the coach is waiting on *that* season and we can name it.
// After a season closes, that season is finished and they're waiting on a next
// one that may not exist in Whisper yet — telling them reservations for it
// "aren't open yet" would describe a season that already ended.
function seasonCopy(season) {
  const name = seasonDisplayName(season);
  if (season.fields_close_date && todayET() > season.fields_close_date) {
    return {
      lede: [
        el('strong', {}, name),
        ' has wrapped up, and reservations for the next season aren’t open yet' +
        ' — we’re still finalizing the schedule and team assignments.',
      ],
      lastItem: 'Check back once the next season’s schedule is posted',
    };
  }
  return {
    lede: [
      'Reservations aren’t open for ',
      el('strong', {}, name),
      ' yet — we’re still finalizing the schedule and team assignments.',
    ],
    lastItem: 'Check back once the ' + season.type + ' schedule is posted',
  };
}

function meantimeItem(text) {
  return el('div', { className: 'off-season-modal-item' },
    el('span', { className: 'off-season-modal-dot' }),
    el('span', {}, text),
  );
}

// Only surfaces a date the coach can actually wait for. A completed season
// (past its fields-close date) also reports is_accepting_requests: false, and
// its reservations_start_date is months behind us — announcing that would be
// worse than saying nothing at all.
function bookingOpensLabel(season) {
  const d = season.reservations_start_date;
  if (!d || d <= todayET()) return null;
  const parsed = new Date(d + 'T12:00:00');
  if (isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}
